// src/hooks/useLookupReady.js
// Single source of truth for "has the lookup API finished loading?".
//
// Priority order enforced across the app:
//   1) /login (MSAL)  — handled by RequireAuth
//   2) lookup-api     — RequireAuth writes tb_lookup + tb_lookup_at on success
//   3) everything else — hinge-list, hinge-lookup, hinge-search,
//                         manage-request-api, npw-search-api
//
// Downstream data APIs must not fire before step 2. They are DELAYED (queued),
// not permanently blocked: once the lookup response lands they run immediately.
// A fail-open deadline guarantees the UI can never get stuck if the signal is
// missing/misconfigured — after the deadline we proceed anyway. No role checks.
import { useEffect, useRef, useState } from "react";

const SS = typeof window !== "undefined" ? window.sessionStorage : null;
const LOOKUP_KEY = "tb_lookup";
const LOOKUP_AT_KEY = "tb_lookup_at";

// Never hold downstream APIs longer than this once we start waiting.
const MAX_WAIT_MS = 8000;

// Module-level so a single app-wide lookup shares one deadline across all consumers.
let firstNotReadyAt = 0;

/**
 * Synchronous readiness check. Safe to call from any code path (guards, fetchers).
 * Ready when the lookup API persisted its completion marker (tb_lookup_at), or a
 * non-empty profile is present. Fail-opens after MAX_WAIT_MS so it can only delay.
 */
export function isLookupReady() {
    if (!SS) return true; // SSR / no storage: don't block.

    let done = false;
    try {
        // tb_lookup_at is written by RequireAuth immediately after a successful lookup.
        if (SS.getItem(LOOKUP_AT_KEY)) {
            done = true;
        } else {
            const raw = SS.getItem(LOOKUP_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                const prof =
                    parsed && parsed.response && typeof parsed.response === "object"
                        ? parsed.response
                        : parsed;
                done = !!prof && typeof prof === "object" && Object.keys(prof).length > 0;
            }
        }
    } catch {
        done = false;
    }

    if (done) {
        firstNotReadyAt = 0;
        return true;
    }

    // Fail-open: start the clock on first miss; release once the deadline passes.
    if (!firstNotReadyAt) firstNotReadyAt = Date.now();
    return Date.now() - firstNotReadyAt >= MAX_WAIT_MS;
}

/**
 * React hook returning `true` once the lookup API has finished loading (or the
 * fail-open deadline elapsed). Re-renders the consumer when readiness flips, then
 * stops polling. Cleans up its interval + storage listener on unmount.
 */
export function useLookupReady(pollMs = 200) {
    const [ready, setReady] = useState(() => isLookupReady());
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (ready) return undefined;

        const check = () => {
            if (mountedRef.current && isLookupReady()) setReady(true);
        };

        check();
        const id = setInterval(check, pollMs);
        const onStorage = (e) => {
            if (!e || e.storageArea !== SS) return;
            if (e.key === LOOKUP_KEY || e.key === LOOKUP_AT_KEY || e.key == null) check();
        };
        window.addEventListener("storage", onStorage);

        return () => {
            clearInterval(id);
            window.removeEventListener("storage", onStorage);
        };
    }, [ready, pollMs]);

    return ready;
}
